import { useFirebase } from "context/firebase.context";
import { getIdToken } from "firebase/auth";
import _ from "lodash";
import { useCallback, useMemo } from "react";

const BACKEND_SERVER = process.env.REACT_APP_ADMIN_API_BASE_URL;

const useAPI = () => {
  const { auth } = useFirebase();

  const apiRequest = useCallback(
    async (endpoint, options) => {
      if (!auth.currentUser) return null;
      const token = await getIdToken(auth.currentUser);
      return fetch(`${BACKEND_SERVER}${endpoint}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }).then(async (response) => {
        const accepted = [200, 201];
        if (accepted.includes(response.status)) return await response.json();
        if (response.status === 204) return null;
        const responseBody = await response.text();
        try {
          return JSON.parse(responseBody);
        } catch (error) {
          return responseBody;
        }
      });
    },
    [auth.currentUser],
  );

  // let apiRequest to be a dependency of api object,
  // so that when apiRequest change (aka auth changed),
  // all api functions get updated
  const api = useMemo(() => ({ apiRequest }), [apiRequest]);

  api.claimVideoToReview = useCallback(
    async () =>
      apiRequest(`/review/videos/claim`, {
        method: "POST",
      }),
    [apiRequest],
  );

  api.getDepositsAnalytics = useCallback(
    async (start) => apiRequest(`/analytics/deposits?start=${start}`),
    [apiRequest],
  );

  api.getReviewPendingVideosCount = useCallback(
    async () => apiRequest(`/review/videos/count`),
    [apiRequest],
  );

  api.reviewVideo = useCallback(
    async (id, payload) =>
      apiRequest(`/videos/${id}/review`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.createCreator = useCallback(
    async (payload) =>
      apiRequest(`/creators/`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.getCreator = useCallback(
    async (id) => apiRequest(`/creators/${id}`),
    [apiRequest],
  );

  api.getCreators = useCallback(
    async () => apiRequest(`/creators`),
    [apiRequest],
  );

  api.getCreatorTransactions = useCallback(
    async (id) => apiRequest(`/creators/${id}/transactions`),
    [apiRequest],
  );

  api.getCreatorTxsAnalytics = useCallback(
    async (id) => apiRequest(`/creators/${id}/transactions/analytics`),
    [apiRequest],
  );

  api.getVideo = useCallback(
    async (id) => apiRequest(`/videos/${id}`),
    [apiRequest],
  );

  api.getVideos = useCallback(
    async (next) => apiRequest(`/videos?next=${next}`),
    [apiRequest],
  );

  api.deleteVideo = useCallback(
    async (id) => apiRequest(`/videos/${id}`, { method: "DELETE" }),
    [apiRequest],
  );

  api.getCreatorVideos = useCallback(
    async (id, options) => {
      const queries = [];
      const next = _.get(options, "next");
      if (next) queries.push(`next=${next}`);
      return apiRequest(`/creators/${id}/videos?${queries.join("&")}`);
    },
    [apiRequest],
  );

  api.resetVideoSearchEngine = useCallback(
    async () =>
      apiRequest(`/search-engine/videos`, {
        method: "DELETE",
      }),
    [apiRequest],
  );

  api.resetCreatorSearchEngine = useCallback(
    async () =>
      apiRequest(`/search-engine/creators`, {
        method: "DELETE",
      }),
    [apiRequest],
  );

  api.getCreatorFollowers = useCallback(
    async (id) => apiRequest(`/creators/${id}/followers`),
    [apiRequest],
  );

  api.createNotification = useCallback(
    async (payload) =>
      apiRequest(`/notifications`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.updateNotification = useCallback(
    async (id, payload) =>
      apiRequest(`/notifications/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.getNotifications = useCallback(
    async () => apiRequest(`/notifications`),
    [apiRequest],
  );

  api.getNotification = useCallback(
    async (id) => apiRequest(`/notifications/${id}`),
    [apiRequest],
  );

  api.getDeposit = useCallback(
    async (id) => apiRequest(`/deposits/${id}`),
    [apiRequest],
  );
  api.deposit = useCallback(
    async (id, amount) =>
      apiRequest(`/users/${id}/deposits`, {
        method: "POST",
        body: JSON.stringify({ amount }),
      }),
    [apiRequest],
  );

  api.getUser = useCallback(
    async (id) => apiRequest(`/users/${id}`),
    [apiRequest],
  );
  api.getTransaction = useCallback(
    async (id) => apiRequest(`/transactions/${id}`),
    [apiRequest],
  );
  api.getCreatorChats = useCallback(
    async (id) => apiRequest(`/creators/${id}/chatrooms`),
    [apiRequest],
    [],
  );

  api.getChatMessages = useCallback(
    async (id) => apiRequest(`/chatrooms/${id}/messages`),
    [apiRequest],
    [],
  );

  api.claimPreRegistrationReward = useCallback(
    async (payload) =>
      apiRequest(`/pre-registration-reward/claim`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.getGuestModeVideos = useCallback(
    async () => apiRequest(`/guest-mode/videos`),
    [apiRequest],
  );

  api.addGuestModeVideo = useCallback(
    async (id) => apiRequest(`/guest-mode/videos/${id}`, { method: "POST" }),
    [apiRequest],
  );

  api.getExploreSettings = useCallback(
    async () => apiRequest(`/explore`),
    [apiRequest],
  );

  api.getExploreSettingsByChunk = useCallback(
    async (index, offset = 0, limit = 5) =>
      apiRequest(`/explore/chunks/${index}?offset=${offset}&limit=${limit}`),
    [apiRequest],
  );

  api.createExploreSettingsBanner = useCallback(
    async (payload) =>
      apiRequest(`/explore/banners`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.removeExploreSettingsBanner = useCallback(
    async (index) =>
      apiRequest(`/explore/banners/${index}`, { method: "DELETE" }),
    [apiRequest],
  );

  api.removeExploreSettingsGroup = useCallback(
    async (index) =>
      apiRequest(`/explore/groups/${index}`, { method: "DELETE" }),
    [apiRequest],
  );

  api.createExploreSettingsGroup = useCallback(
    async (payload) =>
      apiRequest(`/explore/groups`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.swapExploreSettingsGroup = useCallback(
    async (first, second) =>
      apiRequest(`/explore/swap-groups`, {
        method: "PATCH",
        body: JSON.stringify({ first, second }),
      }),
    [apiRequest],
  );

  api.updateExploreSettingsGroup = useCallback(
    async (index, payload) =>
      apiRequest(`/explore/groups/${index}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    [apiRequest],
  );

  api.removeGuestModeVideo = useCallback(
    async (id) => apiRequest(`/guest-mode/videos/${id}`, { method: "DELETE" }),
    [apiRequest],
  );

  api.getTestAccounts = useCallback(
    async () => apiRequest(`/developers/accounts`),
    [apiRequest],
  );

  api.grantTestAccountPoints = useCallback(
    async (index, amount) =>
      apiRequest(`/developers/accounts/${index}/grant`, {
        method: "POST",
        body: JSON.stringify({ amount }),
      }),
    [apiRequest],
  );

  api.cancelVIP = useCallback(
    async (id) =>
      apiRequest(`/users/${id}/vip/cancel`, {
        method: "POST",
      }),
    [apiRequest],
  );

  api.deleteVIP = useCallback(
    async (id) =>
      apiRequest(`/users/${id}/vip`, {
        method: "DELETE",
      }),
    [apiRequest],
  );

  api.disableUser = useCallback(
    async (id) =>
      apiRequest(`/users/${id}/disable`, {
        method: "POST",
      }),
    [apiRequest],
  );

  api.enableUser = useCallback(
    async (id) =>
      apiRequest(`/users/${id}/enable`, {
        method: "POST",
      }),
    [apiRequest],
  );

  api.getUserStatus = useCallback(
    async (id) => apiRequest(`/users/${id}/status`),
    [apiRequest],
  );

  api.updateUserPhoneNumber = useCallback(
    async (id, phoneNumber) =>
      apiRequest(`/users/${id}/update-phone`, {
        method: "POST",
        body: JSON.stringify({phoneNumber}),
      }),
    [apiRequest],
  );

  api.getStreamBans = useCallback(
    async (options = {}) => {
      const queries = [];
      const { limit = 50, nextCursor, includeExpired = false, creatorId } = options;
      
      if (limit !== 50) queries.push(`limit=${limit}`);
      if (nextCursor) queries.push(`nextCursor=${nextCursor}`);
      if (includeExpired) queries.push(`includeExpired=${includeExpired}`);
      if (creatorId) queries.push(`creatorId=${creatorId}`);
      
      return apiRequest(`/banned-stream-user?${queries.join("&")}`);
    },
    [apiRequest],
  );

  api.banStreamUser = useCallback(
    async (creatorId, userId, reason, expiresAt = null) =>
      apiRequest(`/banned-stream-user/${creatorId}`, {
        method: "POST",
        body: JSON.stringify({ userId, reason, expiresAt }),
      }),
    [apiRequest],
  );

  api.globalBanStreamUser = useCallback(
    async (userId, reason, expiresAt = null) =>
      apiRequest(`/banned-stream-user`, {
        method: "POST",
        body: JSON.stringify({ userId, reason, expiresAt }),
      }),
    [apiRequest],
  );

  api.unbanStreamUser = useCallback(
    async (userId, creatorId) =>
      apiRequest(`/banned-stream-user/${userId}${creatorId ? `?creatorId=${creatorId}` : ""}`, {
        method: "DELETE",
      }),
    [apiRequest],
  );

  api.banCreatorStream = useCallback(
    async (creatorId, reason) =>
      apiRequest(`/creators/${creatorId}/ban-stream`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    [apiRequest],
  );

  api.unbanCreatorStream = useCallback(
    async (creatorId) =>
      apiRequest(`/creators/${creatorId}/ban-stream`, {
        method: "DELETE",
      }),
    [apiRequest],
  );

  api.getCreatorStreamRecords = useCallback(
    async (params) => {
      const { creatorId, startDate, endDate, nextCursor } = params;
      const queries = [];
      
      if (creatorId) queries.push(`creatorId=${creatorId}`);
      if (startDate) {
        // Convert ISO string to Unix timestamp (seconds)
        const fromTimestamp = Math.floor(new Date(startDate).getTime() / 1000);
        queries.push(`from=${fromTimestamp}`);
      }
      if (endDate) {
        // Convert ISO string to Unix timestamp (seconds)
        const toTimestamp = Math.floor(new Date(endDate).getTime() / 1000);
        queries.push(`to=${toTimestamp}`);
      }
      if (nextCursor) queries.push(`nextCursor=${nextCursor}`);
      
      const url = `/stream-records?${queries.join("&")}`;
      console.log('API request URL:', url);
      
      return apiRequest(url);
    },
    [apiRequest],
  );

  api.getCreatorStreamRecordsStatistics = useCallback(
    async (params) => {
      const { creatorId, startDate, endDate } = params;
      const queries = [];
      
      if (creatorId) queries.push(`creatorId=${creatorId}`);
      if (startDate) {
        // Convert ISO string to Unix timestamp (seconds)
        const fromTimestamp = Math.floor(new Date(startDate).getTime() / 1000);
        queries.push(`from=${fromTimestamp}`);
      }
      if (endDate) {
        // Convert ISO string to Unix timestamp (seconds)
        const toTimestamp = Math.floor(new Date(endDate).getTime() / 1000);
        queries.push(`to=${toTimestamp}`);
      }
      
      const url = `/stream-records/statistics?${queries.join("&")}`;
      console.log('Statistics API request URL:', url);
      
      return apiRequest(url);
    },
    [apiRequest],
  );

  api.getStreamVodFiles = useCallback(
    async (streamId) => {
      return apiRequest(`/stream-records/${streamId}/vod-files`);
    },
    [apiRequest],
  );

  api.getStreamVodFileDownloadUrl = useCallback(
    async (streamId, fileName) => {
      return apiRequest(`/stream-records/${streamId}/vod-files/${fileName}/download`);
    },
    [apiRequest],
  );

  api.getStreamRecordTimeline = useCallback(
    async (streamId) => apiRequest(`/stream-records/${streamId}/timeline`),
    [apiRequest],
  );

  api.getStreamRecordIntervalRevenue = useCallback(
    async (streamId, startTime, endTime) => {
      const queries = [];
      queries.push(`startTime=${startTime}`);
      queries.push(`endTime=${endTime}`);
      
      return apiRequest(`/stream-records/${streamId}/interval-revenue?${queries.join("&")}`);
    },
    [apiRequest],
  );

  api.listPosts = useCallback(
    async (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.set("limit", params.limit);
      if (params.next) queryParams.set("next", params.next);
      const queryString = queryParams.toString();
      return apiRequest(`/posts${queryString ? `?${queryString}` : ""}`);
    },
    [apiRequest],
  );

  api.getCreatorPosts = useCallback(
    async (creatorId, params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.set("limit", params.limit);
      if (params.next) queryParams.set("next", params.next);
      const queryString = queryParams.toString();
      return apiRequest(`/creators/${creatorId}/posts${queryString ? `?${queryString}` : ""}`);
    },
    [apiRequest],
  );

  api.denyPost = useCallback(
    async (postId, reason) =>
      apiRequest(`/posts/${postId}/deny`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    [apiRequest],
  );

  return useMemo(() => api, [api]);
};

export default useAPI;
